import { Box, Button, Grid, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  fileNameFormat,
  getMatchingSearchResults,
  getPrimaryRegulator,
  isDefinedAndTrue,
} from "../../common/services/Utilities";
import { AppUrl } from "../../config";
import BankFindLocations from "../../containers/BankFindLocations";
import BankFindSort from "../../containers/BankFindSort";
import ResultPagination from "../../containers/ResultPagination";
import {
  Column,
  KeyValue,
  Query,
  ResultData,
  ResultHighlight,
  ResultRecord,
} from "../../interfaces";
import ActiveStatus from "../ActiveStatus";
import ApiDataDownload from "../ApiDataDownload";
import LeavingFDICDialog from "../LeavingFDICDialog";
import LoadingIndicator from "../LoadingIndicator";
import ModalDialog from "../ModalDialog";
import SearchResultCard, {
  CardListCondensedItem,
} from "../SearchResultCard/SearchResultCard";
import ViewInstitutionModalSubtitle from "../ViewInstitutionModalSubtitle";
import BankFindDisruptor from "./BankFindDisruptor";
import ViewDetailsButton from "./ViewDetailsButton";

export interface Props {
  appState: Query;
  resultData: ResultData | null;
  resultHighlight: ResultHighlight;
  recordCount: number;
  dataLoaded: boolean;
  searchPills: React.ReactElement;
  locationValues?: string;
  dataDownloadPost?: KeyValue;
}

const useStyles = makeStyles({
  results: {
    margin: "0 .5rem",
  },
  activeIcon: {
    stroke: "#0ca900",
  },
  loadingSpinner: {
    marginTop: 24,
    width: "100%",
    textAlign: "center",
  },
  loadingText: {
    fontSize: 20,
    width: "100%",
    textAlign: "center",
  },
  centerGridContent: {
    "& .MuiGrid-item": {
      margin: "0 auto",
    },
  },
  gridBlock: {
    paddingBottom: "1rem",
  },
  columnContainer: {
    marginTop: 8,
  },
  modalSubtitle: {
    paddingBottom: 10,
  },
  buttonGroup: {
    "minWidth": 200,
    "display": "inherit",
    "margin": "0 auto",
    "& .MuiButton-root": {
      margin: "16px 8px 0px 8px",
      width: 200,
      height: 40,
    },
  },
});

export const RecordCount = (recordCount: { [key: string]: number }) => {
  return (
    <Typography variant="h2" id="recordCount">
      {recordCount.recordCount.toLocaleString(navigator.language, {
        minimumFractionDigits: 0,
      })}{" "}
      {recordCount.recordCount === 1 ? "Matching Result" : "Matching Results"}
    </Typography>
  );
};

export default function BankFindResults({
  appState,
  resultData,
  resultHighlight,
  recordCount,
  dataLoaded,
  searchPills,
  locationValues,
  dataDownloadPost,
}: Props) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [selectedResultRecord, setSelectedResultRecord] = React.useState<
    ResultRecord
  >();
  const history = useHistory();
  const handleViewMatchingOffices = (event: any, record: ResultRecord) => {
    event.preventDefault();
    setOpen(true);
    setSelectedResultRecord(record);
  };

  const showViewMatchingOffices =
    (appState.filters.zip || appState.filters.stalp || appState.search.city) &&
    appState.ui.branchOffices;

  const bankFindLoading = (
    <LoadingIndicator
      text="Loading"
      spinnerSize={32}
      textClass={classes.loadingText}
      spinnerClass={classes.loadingSpinner}
    />
  );
  if (resultData === null) {
    return bankFindLoading;
  } else {
    const BankFindResultsList = resultData.map(
      (resultRecord: ResultRecord, index: number) => {
        const leftColumn = <ActiveStatus institution={resultRecord} />;

        const centerColumn = (
          <>
            <Typography component="div" className={classes.gridBlock}>
              <CardListCondensedItem
                itemLabel="FDIC Cert #"
                itemValue={resultRecord.CERT}
              />
            </Typography>
            <Typography component="div" className={classes.gridBlock}>
              <CardListCondensedItem
                itemLabel="Primary Regulator"
                itemValue={getPrimaryRegulator(resultRecord.REGAGNT)}
              />
            </Typography>
          </>
        );

        const rightColumn = (
          <>
            <Typography component="div" className={classes.gridBlock}>
              <CardListCondensedItem
                itemLabel="Corporate Headquarters"
                itemValue={resultRecord.ADDRESS}
              />
              {isDefinedAndTrue(resultRecord.ADDRESS2) ? (
                <CardListCondensedItem
                  itemValueId={`address_line_2_${resultRecord.CERT}`}
                  itemValue={resultRecord.ADDRESS2}
                />
              ) : (
                ""
              )}
              <CardListCondensedItem
                itemValue={`${resultRecord.CITY}, ${resultRecord.STALP} ${resultRecord.ZIP}`}
              />
            </Typography>
            {resultRecord.WEBADDR && resultRecord.ACTIVE ? (
              <Typography
                className={clsx("primary-website", classes.gridBlock)}
                component="div"
              >
                <CardListCondensedItem
                  itemLabel="Primary Website"
                  itemValue=""
                />
                <LeavingFDICDialog
                  bankName={resultRecord.NAME}
                  webAddress={resultRecord.WEBADDR}
                />
              </Typography>
            ) : (
              ""
            )}
          </>
        );

        const rightSection = (
          <Grid container>
            <Grid item md={5} xs={12} sm={6}>
              {centerColumn}
            </Grid>
            <Grid item md={7} xs={12} sm={6}>
              {rightColumn}
            </Grid>
          </Grid>
        );

        const columns: Column[] = [
          {
            widthMd: 4,
            widthXs: 6,
            widthSm: 5,
            content: leftColumn,
            columnClass: classes.centerGridContent,
          },
          { widthMd: 8, widthXs: 6, widthSm: 7, content: rightSection },
        ];

        const searchMatches = getMatchingSearchResults(resultHighlight[index]);
        return (
          <div key={`BankFind-${resultRecord.CERT}`}>
            <SearchResultCard
              id={`search-result-card-${index}`}
              title={resultRecord.NAME}
            >
              <Grid container spacing={2} className={classes.columnContainer}>
                {columns.map((column: Column, colummIndex: number) => {
                  return (
                    <Grid
                      key={`column-${resultRecord.CERT}-${colummIndex}`}
                      item
                      md={column.widthMd}
                      xs={column.widthXs}
                      sm={column.widthSm}
                      className={
                        column.columnClass
                          ? clsx("result-card-column", column.columnClass)
                          : "result-card-column"
                      }
                    >
                      {column.content}
                    </Grid>
                  );
                })}
              </Grid>
              <Box display="flex">
                <Box className={classes.buttonGroup}>
                  <ViewDetailsButton
                    name={resultRecord.NAME}
                    cert={resultRecord.CERT}
                    url={AppUrl("BankFind") + window.location.search}
                  />
                  {resultRecord.ACTIVE ? (
                    <Button
                      aria-label={`View ${
                        showViewMatchingOffices ? "Matching" : "All"
                      } Offices for ${resultRecord.NAME}.`}
                      id={`view-offices-button-${resultRecord.CERT}`}
                      className="viewOfficesButton"
                      onClick={(event: any) =>
                        handleViewMatchingOffices(event, resultRecord)
                      }
                      variant="contained"
                      disableElevation
                      color="secondary"
                    >
                      {`${
                        showViewMatchingOffices ? "Matching Branch" : "View All"
                      } Locations`}
                    </Button>
                  ) : (
                    undefined
                  )}
                </Box>
              </Box>
            </SearchResultCard>
            {searchMatches.count ? (
              <BankFindDisruptor
                cert={resultRecord.CERT}
                searchValue={
                  appState.search.name
                    ? appState.search.name
                    : appState.search.url
                }
                searchMatches={searchMatches}
              />
            ) : (
              undefined
            )}
          </div>
        );
      },
    );

    const modalSubtitle = (
      <div className={classes.modalSubtitle}>
        Branch Office Information
        <ViewInstitutionModalSubtitle
          onClick={() => {
            history.push({
              pathname: `${AppUrl("BankFind")}/details/${
                selectedResultRecord ? selectedResultRecord.CERT : ""
              }`,
              state: {
                url: `${AppUrl("BankFind")}${window.location.search}`,
              },
            });
          }}
        />
      </div>
    );

    return (
      <>
        <Box
          className={classes.results}
          color="primary.main"
          id="searchResults"
        >
          <Typography
            component="div"
            style={{
              position: "relative",
            }}
          >
            <RecordCount recordCount={recordCount} />
            <ApiDataDownload
              post={dataDownloadPost}
              app="bankFind"
              apiPathLink="institutions"
              numberOfResults={recordCount}
              apiData={[
                {
                  appState: appState,
                  apiPath: "institutions",
                  fileName: `${fileNameFormat("Institutions")}.csv`,
                },
              ]}
            />
          </Typography>
          <Divider />
          {searchPills}
          <ResultPagination count={recordCount}>
            <>
              <BankFindSort />
              {!dataLoaded ? bankFindLoading : BankFindResultsList}
            </>
          </ResultPagination>
        </Box>
        <ModalDialog
          title={
            selectedResultRecord
              ? `${selectedResultRecord.NAME} - #${selectedResultRecord.CERT}`
              : ""
          }
          display={open}
          setDisplay={setOpen}
          subtitle={modalSubtitle}
          maxWidth="lg"
        >
          <BankFindLocations
            cert={selectedResultRecord ? selectedResultRecord.CERT : ""}
            locationsSearch={locationValues}
          />
        </ModalDialog>
      </>
    );
  }
}
